/**
 * Встановлення сайту як застосунку.
 *
 * Chromium (Chrome, Edge, Samsung, Opera) сам вирішує, чи можна ставити, і
 * віддає подію beforeinstallprompt — лише якщо застосунок ще не встановлений.
 * Тому там банер показуємо тільки після цієї події, а кнопка відкриває
 * системний діалог. Подію ловить плагін installPrompt.client.js на старті:
 * вона приходить один раз за завантаження сторінки, і людина могла зайти не
 * з головної.
 *
 * Safari (iOS, macOS) і Firefox на Android діалогу не мають — показуємо, як
 * додати вручну. Чи встановлено, з вкладки браузера там не дізнатись, тож
 * лишається хрестик. Firefox на компʼютері ставити не вміє — банера немає.
 */

const HIDE_KEY = 'pwaBannerHiddenAt'
const INSTALLED_KEY = 'pwaInstalled'
const HIDE_FOR = 30 * 24 * 60 * 60 * 1000

// Стан модуля, а не useState: подія не серіалізується в payload SSR
const deferred = shallowRef(null)
const installed = ref(false)

const read = (key) => {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}
const write = (key, value) => {
  try {
    localStorage.setItem(key, value)
  } catch (e) {}
}

export const isStandalone = () =>
  ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay'].some(
    (mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches
  ) || window.navigator.standalone === true

// Яку підказку показати там, де немає beforeinstallprompt
const manualPlatform = () => {
  const ua = navigator.userAgent
  const iOS =
    /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  // У вбудованих браузерах (Telegram, Instagram…) на iOS немає «На
  // початковий екран», і в UA зазвичай бракує «Safari»
  if (iOS) return /Safari/.test(ua) ? 'ios' : null
  if (/Android/.test(ua) && /Firefox/.test(ua)) return 'firefox-android'
  // Safari на macOS 17+ — «Додати в Dock»; Chrome на маку теж має «Safari»
  // в UA, але його відсіює наявність beforeinstallprompt
  if (/Macintosh/.test(ua) && /Version\/(1[7-9]|[2-9]\d)/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg|Firefox/.test(ua)) {
    return 'mac-safari'
  }
  return null
}

/** Викликає плагін на старті клієнта */
export const initPwaInstall = () => {
  if (read(INSTALLED_KEY)) installed.value = true

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred.value = event
    // Браузер пропонує встановити — отже, зараз не встановлено (могли видалити)
    installed.value = false
    try {
      localStorage.removeItem(INSTALLED_KEY)
    } catch (e) {}
  })

  window.addEventListener('appinstalled', () => {
    deferred.value = null
    installed.value = true
    write(INSTALLED_KEY, '1')
  })

  // Chrome на Android і компʼютері знає про встановлений застосунок з того
  // самого сайту (related_applications у manifest.json)
  navigator.getInstalledRelatedApps?.()
    .then((apps) => {
      if (apps?.length) installed.value = true
    })
    .catch(() => {})
}

export const usePwaInstall = () => {
  const platform = ref(null)
  const hidden = ref(true)

  onMounted(() => {
    platform.value = manualPlatform()
    const at = Number(read(HIDE_KEY))
    hidden.value = isStandalone() || (at && Date.now() - at < HIDE_FOR)
  })

  const mode = computed(() => {
    if (hidden.value || installed.value) return null
    if (deferred.value) return 'prompt'
    return platform.value
  })

  const install = async () => {
    const event = deferred.value
    if (!event) return
    deferred.value = null
    event.prompt()
    const { outcome } = await event.userChoice.catch(() => ({}))
    // Відмовився в системному діалозі — банер теж ховаємо, не набридаємо
    if (outcome !== 'accepted') dismiss()
  }

  const dismiss = () => {
    hidden.value = true
    write(HIDE_KEY, String(Date.now()))
  }

  return { mode, install, dismiss }
}
